import React, { useEffect, useMemo, useRef, useState } from 'react';
import { COUNTRIES, PRIORITY_ISO, byIso } from '../../utils/countryCodes';
import { ringBorder, C } from '../portalkit';

/*
 * Auth fields — design_handoff_auth_profile_planner §Fields.
 *   Label 11px/700 .14em white, the required * in pink. Box 52px, radius 16, #000, 2px transparent
 *   border + a 1px #2A2A2A inset hairline; on focus the border becomes the ring and the hairline goes.
 *   Hint 10px/600 .1em: #8A8A8A, or #FF66C4 for an error (the hairline turns #5A1A3E).
 */

const labelStyle = { display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, fontWeight: 700, letterSpacing: '.14em', textTransform: 'uppercase', color: '#fff' };

export function boxStyle(focused, error) {
  return {
    height: 52, border: '2px solid transparent', borderRadius: 16, boxSizing: 'border-box',
    background: focused ? ringBorder('#000') : '#000',
    boxShadow: focused ? 'none' : `inset 0 0 0 1px ${error ? C.errorEdge : C.edgeDark}`,
    transition: 'box-shadow .2s',
  };
}

export function Hint({ children, error }) {
  if (!children) return null;
  return <span style={{ fontSize: 10, fontWeight: 600, letterSpacing: '.1em', lineHeight: 1.6, textTransform: 'uppercase', color: error ? C.pinkLight : C.onBlack2 }}>{children}</span>;
}

function Eye({ shown }) {
  return shown ? (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M10.6 5.1A10 10 0 0 1 12 5c6.5 0 10 7 10 7a17 17 0 0 1-2.3 3.2M6.6 6.6A17 17 0 0 0 2 12s3.5 7 10 7a9.7 9.7 0 0 0 5.4-1.6M3 3l18 18" /><path d="M9.9 9.9a3 3 0 0 0 4.2 4.2" /></svg>
  ) : (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z" /><circle cx="12" cy="12" r="3" /></svg>
  );
}

/** A text / email / password / code field. `password` adds the eye that toggles the type. */
export default function AuthField({
  id, label, required, type = 'text', value, onChange, placeholder, hint, hintError = false, edgeError = false,
  inputMode, maxLength, autoComplete, password = false, fontSize = 14, letterSpacing = '.04em', disabled,
}) {
  const [focused, setFocused] = useState(false);
  const [shown, setShown] = useState(false);
  const inputType = password ? (shown ? 'text' : 'password') : type;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <label htmlFor={id} style={labelStyle}>{label}{required && <span style={{ color: C.pink }} aria-hidden="true">*</span>}</label>
      <div style={{ display: 'flex', alignItems: 'center', padding: password ? '0 6px 0 16px' : '0 16px', ...boxStyle(focused, edgeError) }}>
        <input
          id={id}
          className="pk-in pk-in-dark"
          type={inputType}
          value={value}
          onChange={onChange}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          placeholder={placeholder}
          inputMode={inputMode}
          maxLength={maxLength}
          autoComplete={autoComplete}
          disabled={disabled}
          required={required}
          style={{ flex: 1, height: '100%', padding: 0, color: '#fff', fontSize, letterSpacing }}
        />
        {password && (
          <button type="button" onClick={() => setShown((s) => !s)} aria-label={shown ? 'Hide password' : 'Show password'} aria-pressed={shown} className="pk pk-btn pk-eye" style={{ flexShrink: 0, width: 40, height: 40, padding: 0 }}>
            <Eye shown={shown} />
          </button>
        )}
      </div>
      <Hint error={hintError}>{hint}</Hint>
    </div>
  );
}

/**
 * The phone field: one box split in two — a country button (the flag, `+1`, a chevron — the flags
 * stay, as on every phone field in the product) with a 1px divider, then the tel input. Reports `{ country, number }`
 * separately, like PhoneInput: the server resolves the dial code from the ISO country.
 */
export function PhoneField({ id = 'phone', label = 'PHONE', required = false, country = 'CA', number = '', onChange, hint, hintError = false, disabled, tone = 'dark', autoFocus = false, height = 52, radius = 16 }) {
  // tone 'light': the join page's white pill on black — no hairline, no focus ring, the box is the field.
  const light = tone === 'light';
  const ink = light ? '#000' : '#fff';
  const dim = light ? '#9CA3AF' : '#8A8A8A';
  const divider = light ? '#E5E7EB' : '#2A2A2A';
  const [focused, setFocused] = useState(false);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const wrap = useRef(null);
  const search = useRef(null);
  const selected = byIso(country) || byIso('CA');

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (wrap.current && !wrap.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    const t = setTimeout(() => search.current?.focus(), 0);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); clearTimeout(t); };
  }, [open]);

  const list = useMemo(() => {
    const q = query.trim().toLowerCase();
    const match = (c) => !q || c.name.toLowerCase().includes(q) || c.iso.toLowerCase() === q || c.dial.replace('+', '').startsWith(q.replace('+', ''));
    if (q) return COUNTRIES.filter(match);
    const pinned = PRIORITY_ISO.map(byIso).filter(Boolean);
    return [...pinned, ...COUNTRIES.filter((c) => !PRIORITY_ISO.includes(c.iso))];
  }, [query]);

  return (
    <div ref={wrap} style={{ position: 'relative', display: 'flex', flexDirection: 'column', gap: 8 }}>
      {label && <label htmlFor={id} style={labelStyle}>{label}{required && <span style={{ color: C.pink }} aria-hidden="true">*</span>}</label>}
      <div style={light
        ? { display: 'grid', gridTemplateColumns: 'auto minmax(0,1fr)', alignItems: 'center', height, borderRadius: radius, background: '#fff', boxSizing: 'border-box' }
        : { display: 'grid', gridTemplateColumns: 'auto minmax(0,1fr)', alignItems: 'center', ...boxStyle(focused || open, false) }}>
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          disabled={disabled}
          aria-label={`Country: ${selected?.name}`}
          aria-expanded={open}
          className="pk"
          style={{ display: 'flex', alignItems: 'center', gap: 8, height: '100%', padding: '0 14px 0 18px', border: 'none', borderRight: `1px solid ${divider}`, background: 'none', color: ink, fontSize: 13, fontWeight: 700, letterSpacing: '.06em', cursor: 'pointer', fontFamily: 'inherit' }}
        >
          <span aria-hidden="true" style={{ fontSize: 16, lineHeight: 1 }}>{selected?.flag}</span>
          <span>{selected?.dial}</span>
          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke={dim} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M19 9l-7 7-7-7" /></svg>
        </button>
        <input
          id={id}
          className={`pk-in ${light ? 'pk-in-light' : 'pk-in-dark'}`}
          type="tel"
          inputMode="tel"
          autoComplete="tel-national"
          autoFocus={autoFocus}
          value={number}
          onChange={(e) => onChange({ country, number: e.target.value })}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          placeholder="250 555 0143"
          maxLength={24}
          disabled={disabled}
          style={{ height: '100%', padding: '0 16px', color: ink, fontSize: light ? 15 : 14, letterSpacing: '.06em', fontFamily: 'inherit' }}
        />
      </div>
      <Hint error={hintError}>{hint}</Hint>
      {open && (
        <div role="listbox" aria-label="Countries" style={{ position: 'absolute', left: 0, right: 0, top: 'calc(100% + 6px)', zIndex: 50, borderRadius: 16, background: light ? '#fff' : '#111', boxShadow: light ? '0 12px 32px rgba(0,0,0,.45)' : 'inset 0 0 0 1px #2A2A2A', overflow: 'hidden' }}>
          <input
            ref={search}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search countries"
            aria-label="Search countries"
            className={`pk-in ${light ? 'pk-in-light' : 'pk-in-dark'}`}
            style={{ width: '100%', height: 44, padding: '0 16px', color: ink, fontSize: 13, borderBottom: `1px solid ${divider}`, boxSizing: 'border-box', fontFamily: 'inherit' }}
          />
          <div style={{ maxHeight: 240, overflowY: 'auto' }}>
            {list.length === 0 ? (
              <div style={{ padding: '14px 16px', fontSize: 11, color: dim, letterSpacing: '.1em', textTransform: 'uppercase' }}>No match.</div>
            ) : list.map((c) => {
              const on = c.iso === country;
              return (
                <button
                  key={c.iso}
                  type="button"
                  role="option"
                  aria-selected={on}
                  onClick={() => { setOpen(false); setQuery(''); onChange({ country: c.iso, number }); }}
                  className="pk"
                  style={{ display: 'flex', alignItems: 'center', gap: 12, width: '100%', padding: '10px 16px', border: 'none', background: on ? (light ? '#000' : '#262626') : 'transparent', color: on && light ? '#fff' : ink, fontSize: 13, textAlign: 'left', cursor: 'pointer', fontFamily: 'inherit' }}
                >
                  <span aria-hidden="true" style={{ width: 22, flexShrink: 0, fontSize: 16, lineHeight: 1 }}>{c.flag}</span>
                  <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.name}</span>
                  <span style={{ flexShrink: 0, color: on && light ? 'rgba(255,255,255,.7)' : dim }}>{c.dial}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
